import {
    Chip,
    Grid2,
    IconButton,
    Table,
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SwitchRightIcon from '@mui/icons-material/SwitchRight';
import SwitchLeftIcon from '@mui/icons-material/SwitchLeft';
import GroupIcon from '@mui/icons-material/Group';
import PaymentIcon from '@mui/icons-material/Payment';
import ExtensionIcon from '@mui/icons-material/Extension';
import { Link } from 'react-router-dom';
import SkeletonTable from '../../../components/atom/SkeletonTable';
import SortableTableCell from '../../../components/atom/SortableTableCell';
import { useAuth } from '../../../hooks/useAuth';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { useTranslation } from 'react-i18next';
import VenuesPagination from './VenuesPagination';
import useVenueActions from '../hooks/useVenueActions';

const VenuesTableView = ({
    rows,
    limit,
    page,
    setParams,
    total,
    loading,
    reloadData,
    sort,
    sortDir,
    setSorting,
}) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { can, CAPABILITIES } = useCapabilities();
    const { confirmDelete, switchVenue, isDeleting, isSwitching } = useVenueActions(reloadData);

    return (
        <Grid2 size={{ xs: 12 }}>
            <TableContainer>
                <Table sx={{ minWidth: 700 }}>
                    <TableHead>
                        <TableRow>
                            <SortableTableCell
                                arrow={sort === 'name' ? sortDir : ''}
                                onClick={() => setSorting('name')}
                            >
                                {t('venues.columns.name')}
                            </SortableTableCell>
                            <SortableTableCell
                                arrow={sort === 'site' ? sortDir : ''}
                                onClick={() => setSorting('site')}
                            >
                                {t('venues.columns.site')}
                            </SortableTableCell>
                            <SortableTableCell
                                arrow={sort === 'allowedSplitPaymentAmount' ? sortDir : ''}
                                onClick={() => setSorting('allowedSplitPaymentAmount')}
                            >
                                {t('venues.columns.allowedSplitPaymentAmount')}
                            </SortableTableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <SkeletonTable rows={limit} cells={4} />
                        ) : (
                            rows?.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>{row.site}</TableCell>
                                    <TableCell>{row.allowedSplitPaymentAmount}</TableCell>
                                    <TableCell align="right">
                                        {user?.venueId === row.id && (
                                            <Chip label={t('venues.isCurrent')} color="success" />
                                        )}
                                        {can(CAPABILITIES.PAYMENT_METHODS_VIEW) && (
                                            <Link to={`/venues/${row.id}/paymentmethods`}>
                                                <Tooltip title={t('paymentmethods.title')}>
                                                    <IconButton>
                                                        <PaymentIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Link>
                                        )}
                                        {!can(CAPABILITIES.USERS_MANAGE) ? null : (
                                            <Link to={`/venues/${row.id}/users`}>
                                                <Tooltip title={t('users.title')}>
                                                    <IconButton>
                                                        <GroupIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Link>
                                        )}
                                        {can(CAPABILITIES.INTEGRATIONS_MANAGE) && (
                                            <Link to={`/venues/${row.id}/integrations`}>
                                                <Tooltip title={t('integrations.title')}>
                                                    <IconButton>
                                                        <ExtensionIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Link>
                                        )}
                                        {!can(CAPABILITIES.VENUES_MANAGE) ? null : (
                                            <>
                                                <Link to={`/venues/edit/${row.id}`}>
                                                    <Tooltip title={t('common.edit')}>
                                                        <IconButton>
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Link>
                                                <Tooltip
                                                    title={
                                                        user?.venueId === row.id
                                                            ? t('venues.isCurrent')
                                                            : t('users.switchToVenue')
                                                    }
                                                >
                                                    <span>
                                                        <IconButton
                                                            disabled={
                                                                user?.venueId === row.id ||
                                                                isSwitching(row.id)
                                                            }
                                                            onClick={() => {
                                                                switchVenue(row.id);
                                                            }}
                                                        >
                                                            {user?.venueId === row.id ? (
                                                                <SwitchRightIcon />
                                                            ) : (
                                                                <SwitchLeftIcon />
                                                            )}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <IconButton
                                                    onClick={() => confirmDelete(row)}
                                                    disabled={isDeleting(row.id)}
                                                >
                                                    <Tooltip title={t('common.delete')}>
                                                        <DeleteIcon />
                                                    </Tooltip>
                                                </IconButton>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <VenuesPagination
                rows={rows}
                total={total}
                limit={limit}
                page={page}
                setParams={setParams}
            />
        </Grid2>
    );
};

export default VenuesTableView;
